import React, { useCallback, useContext, useMemo } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, ITotalPrice } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './IsPreselectedField.module.scss';

interface IIsPreselectedField {
	setPreselectedInRestaurants: (value: string[]) => void;
	isPreselectedInRestaurants: string[];
	totalPrices: ITotalPrice[];
	fetchedRestaurants: IItemForGetShortInfo[];
}

const IsPreselectedField: React.FC<IIsPreselectedField> = ({
	isPreselectedInRestaurants,
	totalPrices,
	fetchedRestaurants,
	setPreselectedInRestaurants,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = isPreselectedInRestaurants.filter((el) => el !== id);
			setPreselectedInRestaurants(updatedSelectedItems);
		},
		[isPreselectedInRestaurants, setPreselectedInRestaurants],
	);

	const restaurantsList = useMemo(() => {
		return isPreselectedInRestaurants.map((el) => {
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			const label = fetchedRestaurants.find((rest) => rest.id === el)?.name || '';
			return (
				<SelectedItem
					key={el}
					label={label}
					onDelete={!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)}
				/>
			);
		});
	}, [isPreselectedInRestaurants, loggedUser, fetchedRestaurants, hasAccess, handleRemove]);

	return (
		<div className={style.container} data-test="IsPreselectedField">
			<div className={style.field}>
				<MultipleSelect
					isPreselectedInRestaurants={isPreselectedInRestaurants}
					totalPrices={totalPrices}
					setPreselectedInRestaurants={setPreselectedInRestaurants}
				/>
			</div>
			<div className={style.restaurantsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(IsPreselectedField);
